import { useEstabsContext } from '../../contexts/EstabsContext';
import { useMemo, useState } from 'react';
import { useParamsContext } from '../../contexts/ParamsContext';
import { formatBRL } from '../agenda/cliente/List/helpers';
import { normalizeSearch } from '../recorrencias/helpers';
import { useVendasContext } from './Context';
import { describeItems, formatDateTime } from './helpers';
import { RecurringSaleDialog } from './RecurringDetail';
import { ChevronRight, RefreshCw, Search, TriangleAlert } from 'lucide-react';

export default function VendasRecorrentes() {
    const { estabSession } = useEstabsContext();
    const { recorrentes, recorrentesLoading, recorrentesError, reloadRecorrentes } = useVendasContext();
    const { setParams } = useParamsContext();
    const [showCanceled, setShowCanceled] = useState(false);
    const [q, setQ] = useState('');
    const [saleTarget, setSaleTarget] = useState<any | null>(null);

    const searched = useMemo(() => {
        const term = normalizeSearch(q);
        if (!term) return recorrentes;

        return recorrentes.filter((row) => normalizeSearch(`${row.clientName ?? ''} ${describeItems(row.items)}`).includes(term));
    }, [recorrentes, q]);

    const canceledCount = searched.filter((row) => row.status === 'CANCELADA').length;
    const activeCount = searched.length - canceledCount;
    const lista = searched.filter((row) => (row.status === 'CANCELADA') === showCanceled);

    if (recorrentesLoading && !recorrentes.length) {
        return (
            <div className="space-y-2" aria-busy="true">
                {[0, 1, 2].map((item) => (
                    <div key={item} className="sf-skeleton h-16" />
                ))}
            </div>
        );
    }

    if (recorrentesError && !recorrentes.length) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar as recorrências</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void reloadRecorrentes()}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!recorrentes.length) {
        return (
            <div className="sf-state">
                <RefreshCw aria-hidden />
                <h2>Nenhuma recorrência</h2>
                <p>Guarde os produtos e preços que um cliente compra sempre para vender com um toque.</p>
            </div>
        );
    }

    return (
        <>
            <div className="sf-toolbar-row">
                <div className="sf-segmented" role="group" aria-label="Situação">
                    <button type="button" aria-pressed={!showCanceled} onClick={() => setShowCanceled(false)}>
                        Ativas
                        <span className="sf-segmented-count">{activeCount}</span>
                    </button>
                    <button type="button" aria-pressed={showCanceled} onClick={() => setShowCanceled(true)}>
                        Canceladas
                        <span className="sf-segmented-count">{canceledCount}</span>
                    </button>
                </div>
                <label className="sf-search">
                    <Search aria-hidden />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar cliente ou produto"
                        aria-label="Buscar por cliente ou produto"
                    />
                </label>
            </div>

            {lista.length === 0 ? (
                <div className="sf-state">
                    <Search aria-hidden />
                    <h2>{q ? 'Nenhuma recorrência encontrada' : showCanceled ? 'Nenhuma recorrência cancelada' : 'Nenhuma recorrência ativa'}</h2>
                </div>
            ) : (
                <ul className="sf-group">
                    {lista.map((row) => (
                        <li key={row._id}>
                            <div className="sf-row !p-0">
                                <button
                                    type="button"
                                    className="sf-row min-w-0 flex-1 !pr-2"
                                    onClick={() => setParams({ action: 'recorrente', id: row._id })}
                                >
                                    <span className="sf-row-body">
                                        <span className="sf-row-title">{row.clientName || 'Cliente'}</span>
                                        <span className="sf-row-sub">{describeItems(row.items)}</span>
                                        <span className="sf-row-sub">
                                            {row.lastSaleAt ? `Última venda: ${formatDateTime(row.lastSaleAt, estabSession?.timezone)}` : 'Sem venda ainda'}
                                        </span>
                                        {row.status === 'PAUSADA' && (
                                            <span className="sf-tags">
                                                <span className="sf-tag" data-tone="gray">
                                                    Pausada
                                                </span>
                                            </span>
                                        )}
                                    </span>
                                    <span className="sf-row-end">{formatBRL(row.recurringTotal)}</span>
                                    <ChevronRight className="sf-chevron" aria-hidden />
                                </button>
                                {row.status !== 'CANCELADA' && (
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm mr-3 shrink-0"
                                        aria-label={`Vender para ${row.clientName || 'o cliente'}`}
                                        onClick={() => setSaleTarget(row)}
                                    >
                                        Vender
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {!!saleTarget && <RecurringSaleDialog target={saleTarget} onClose={() => setSaleTarget(null)} />}
        </>
    );
}
